/**
 * The right result panel. All engineering values are backend-authored; this
 * component only presents, groups and totals values whose aggregation is
 * explicitly meaningful (fixing counts).
 */
import { useState, type ReactNode } from 'react';

import {
  EmptyState, MessageList, Panel, StatusBadge, Symbol, TabBar, UtilizationBar,
  statusLabel, type TabDefinition,
} from '../shared/components';
import { SectionIcon, sectionIconForTitle } from '../shared/SectionIcon';
import {
  formatArea, formatCount, formatLength, formatQuantity, formatQuantityWithUnit,
  formatUtilization,
} from '../shared/format';
import type {
  CalculationResponse, Length, ModuleResult, Quantity, QuantitySummary, ValidationMessage, ZoneResult,
} from '../gen/ejot/roofing/v1/types';
import { ActionableDiagnostic } from './diagnostics/ActionableDiagnostic';
import { ResultReportSummary } from './ResultReportSummary';
import { formatZoneDisplayLabel } from './wind-zone-plan/zonePresentation';

interface ResultPanelProps {
  result: CalculationResponse | undefined;
  isPending: boolean;
  error: Error | null;
  activeModuleId?: string;
  selectedZoneId?: string;
  onZoneSelect?: (zoneId: string | undefined) => void;
  onOpenVisual?: (view: 'membrane-plan' | 'fixing-layout') => void;
}

export function ResultPanel({
  result,
  isPending,
  error,
  activeModuleId,
  selectedZoneId,
  onZoneSelect,
  onOpenVisual,
}: ResultPanelProps) {
  const [tab, setTab] = useState('results');
  const module = result?.modules?.find((candidate) => candidate.module_id === activeModuleId) ?? result?.modules?.[0];
  const messages = collectMessages(result);
  const warningMessages = warningsOnly(messages);
  const tabs: readonly TabDefinition[] = [
    { id: 'results', label: 'Results' },
    { id: 'accessories', label: 'Accessories' },
    { id: 'warnings', label: 'Warnings', badge: warningMessages.length },
  ];

  return (
    <Panel title="Result">
      <TabBar tabs={tabs} active={tab} onSelect={setTab} ariaLabel="Result sections" />
      <div className="panel-content" id={`panel-${tab}`} role="tabpanel" aria-labelledby={`tab-${tab}`}>
        {isPending && <EmptyState>Calculating…</EmptyState>}
        {error && (
          <div className="message message-error" role="alert">
            <div className="message-title">Calculation service unavailable</div>
            <p className="message-body">{error.message}</p>
          </div>
        )}
        {result?.demonstration_result && <DemonstrationNotice />}
        {!isPending && !error && result && (
          <>
            {tab === 'results' && (
              <ResultsTab
                result={result}
                module={module}
                selectedZoneId={selectedZoneId}
                onZoneSelect={onZoneSelect}
                onOpenVisual={onOpenVisual}
              />
            )}
            {tab === 'accessories' && <EmptyState>Installation tools and accessories are listed once the product catalogue is available.</EmptyState>}
            {tab === 'warnings' && <MessageList messages={warningMessages} emptyText="No warnings." />}
          </>
        )}
      </div>
    </Panel>
  );
}

function DemonstrationNotice() {
  return (
    <div className="message message-error demonstration-notice" role="alert">
      <div className="message-title">Demonstration data</div>
      <p className="message-body">These values are a stored fixture used to develop the interface. Nothing here was calculated, and no part of it may be used for design.</p>
    </div>
  );
}

function ResultsTab({ result, module, selectedZoneId, onZoneSelect, onOpenVisual }: {
  result: CalculationResponse;
  module: ModuleResult | undefined;
  selectedZoneId?: string;
  onZoneSelect?: (zoneId: string | undefined) => void;
  onOpenVisual?: (view: 'membrane-plan' | 'fixing-layout') => void;
}) {
  if (!module) {
    return <ActionableDiagnostic messages={result.messages ?? []} fallback="No module result is available. Complete the required project inputs and product selection so the backend can evaluate this roof." />;
  }

  const incomplete = module.status === 'CALCULATION_STATUS_BLOCKED' || module.status === 'CALCULATION_STATUS_NOT_CALCULATED';
  const etaComponentMode = module.resistance_basis === 'FIXING_RESISTANCE_BASIS_ETA_COMPONENT_CHARACTERISTIC';
  const fraction = module.highest_utilization?.value;
  const visualStatus = incomplete || etaComponentMode
    ? 'is-blocked'
    : fraction !== undefined && fraction > 1 ? 'is-fail' : 'is-pass';
  const statusMessages = [...(result.messages ?? []), ...(module.messages ?? [])];
  const informationReasons = etaComponentMode
    ? []
    : Array.from(new Set((module.zones ?? []).flatMap((zone) => zone.layout?.adjustment_reasons ?? [])));
  const quantities = module.quantities ?? result.project_quantities;

  return (
    <>
      <QuantitySummaryBlock module={module} quantities={quantities} />

      {incomplete && (
        <ResultDisclosure title="Calculation status" defaultOpen>
          <ActionableDiagnostic messages={statusMessages} fallback="The backend could not complete this roof. Review the required inputs and product selection." />
        </ResultDisclosure>
      )}

      <div className={`result-card result-card-compact ${visualStatus}`}>
        <div className="result-card-label heading-with-icon">
          <SectionIcon name="overall" className="section-heading-icon" />
          <span>Overall status</span>
        </div>
        <div className="result-card-value">
          {incomplete || etaComponentMode ? statusLabel(module.status) : formatUtilization(module.highest_utilization)}
        </div>
        <div className="result-card-status">
          <StatusBadge status={module.status} />
        </div>
        {etaComponentMode && (
          <div className="result-card-secondary">ETA pull-out component basis. No verified complete-system PASS verdict is claimed.</div>
        )}
        {!etaComponentMode && <UtilizationBar fraction={fraction} status={module.status} />}
      </div>

      <dl className="result-fast-facts" aria-label="Fast engineering result summary">
        <div className="result-fast-fact">
          <dt>Resistance basis</dt>
          <dd>{describeResistanceBasis(module)}</dd>
        </div>
        <div className="result-fast-fact">
          <dt>Total fixings</dt>
          <dd>{module.quantities?.total_fixings === undefined ? 'Unavailable' : `${formatCount(module.quantities.total_fixings)} piece`}</dd>
        </div>
      </dl>

      {onOpenVisual && (
        <div className="result-visual-actions" role="group" aria-label="Engineering drawings">
          <button type="button" className="toolbar-button" onClick={() => onOpenVisual('membrane-plan')}>Open Membrane Plan</button>
          <button type="button" className="toolbar-button" onClick={() => onOpenVisual('fixing-layout')}>Open Fixing Plan</button>
        </div>
      )}

      <ResultDisclosure title={`Zones (${module.zones?.length ?? 0})`} defaultOpen>
        <ZoneSummaryTable
          zones={module.zones ?? []}
          etaComponentMode={etaComponentMode}
          selectedZoneId={selectedZoneId}
          onZoneSelect={onZoneSelect}
        />
        <details className="zone-details-disclosure">
          <summary>Details</summary>
          <div className="zone-detail-list">
            {(module.zones ?? []).map((zone) => (
              <ZoneRow
                key={zone.zone_id}
                zone={zone}
                selected={zone.zone_id === selectedZoneId}
              />
            ))}
          </div>
        </details>
      </ResultDisclosure>

      <ResultDisclosure title="Advanced Engineering Details">
        {module.wind && (
          <section className="result-section">
            <h3 className="result-section-title">Wind parameters</h3>
            <table className="engineering-table">
              <tbody>
                <WindRow label="Basic wind velocity" q={module.wind.basic_wind_velocity} />
                <WindRow label="Basic velocity pressure" q={module.wind.basic_velocity_pressure} />
                <WindRow label="Exposure factor" q={module.wind.exposure_factor} />
                <WindRow label="Peak velocity pressure" q={module.wind.peak_velocity_pressure} />
                <WindRow label="Internal pressure" q={module.wind.governing_internal_pressure} />
                <WindRow label="Load safety factor" q={module.wind.load_safety_factor} />
              </tbody>
            </table>
          </section>
        )}

        <PressureCaseTrace module={module} />

        {informationReasons.length > 0 && (
          <section className="result-section">
            <h3 className="result-section-title">Installation information</h3>
            <div className="adjustment-reasons">
              <div className="adjustment-reasons-title">Why the installable density differs</div>
              <p>Installable density is derived from the exact fixing pattern and roof-zone geometry, so it can differ from the theoretical density.</p>
              <ul>{informationReasons.map((reason, index) => <li key={index}>{reason}</li>)}</ul>
            </div>
          </section>
        )}

        {result.versions && (
          <section className="result-section">
            <h3 className="result-section-title">Versions</h3>
            <dl className="definition-list">
              <dt>Engine</dt><dd>{result.versions.engine_version ?? '-'}</dd>
              <dt>Catalogue</dt><dd>{result.versions.catalogue_version ?? '-'}</dd>
              <dt>Calculated</dt><dd>{result.calculated_at ?? '-'}</dd>
            </dl>
          </section>
        )}
      </ResultDisclosure>
    </>
  );
}

function QuantitySummaryBlock({ module, quantities }: { module: ModuleResult; quantities: QuantitySummary | undefined }) {
  return (
    <>
      <ResultReportSummary module={module} quantities={quantities} />
      {(quantities?.lines ?? []).length > 0 && (
        <div className="result-product-lines">
          {(quantities?.lines ?? []).map((line, index) => (
            <div className="result-product-line" key={`${line.product_id ?? 'line'}-${index}`}>
              <strong>{line.display_name || line.product_id || 'Product / system'}</strong>
              {line.product_id && line.display_name && <span>{line.product_id}</span>}
              <span>{formatQuantityLine(line.quantity, line.unit)}</span>
            </div>
          ))}
        </div>
      )}
    </>
  );
}

function ResultDisclosure({ title, children, defaultOpen = false }: { title: string; children: ReactNode; defaultOpen?: boolean }) {
  const icon = sectionIconForTitle(title);
  return (
    <details className="result-disclosure" open={defaultOpen || undefined}>
      <summary className="result-disclosure-summary">
        <span className="heading-with-icon">
          {icon && <SectionIcon name={icon} className="section-heading-icon" />}
          <span>{title}</span>
        </span>
      </summary>
      <div className="result-disclosure-body">{children}</div>
    </details>
  );
}

function PressureCaseTrace({ module }: { module: ModuleResult }) {
  const cases = (module.trace?.entries ?? []).filter((entry) => entry.id?.includes('-pressure-case-'));
  if (cases.length === 0) return null;
  return (
    <section className="result-section">
      <h3 className="result-section-title">Pressure cases ({cases.length})</h3>
      <div className="engineering-table-scroll">
        <table className="engineering-table">
          <thead>
            <tr>
              <th scope="col">Case</th>
              <th scope="col">Basis</th>
              <th scope="col" className="numeric"><Symbol symbol="w_tot" /></th>
            </tr>
          </thead>
          <tbody>
            {cases.map((entry) => (
              <tr key={entry.id}>
                <th scope="row">{entry.id}</th>
                <td>{entry.description}</td>
                <td className="numeric">{formatQuantityWithUnit(entry.result)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

type ZoneClassID = 'F' | 'G' | 'H' | 'I';
const ZONE_CLASS_ORDER: readonly ZoneClassID[] = ['F', 'G', 'H', 'I'];

function ZoneSummaryTable({ zones, etaComponentMode, selectedZoneId, onZoneSelect }: {
  zones: readonly ZoneResult[];
  etaComponentMode: boolean;
  selectedZoneId?: string;
  onZoneSelect?: (zoneId: string | undefined) => void;
}) {
  const [openGroups, setOpenGroups] = useState<Set<ZoneClassID>>(() => new Set());
  if (zones.length === 0) return <EmptyState>No zone results.</EmptyState>;

  const groups = groupZones(zones);
  const totalFixings = zones.reduce((sum, zone) => sum + (zone.layout?.total_fixings ?? 0), 0);
  const toggleGroup = (id: ZoneClassID) => setOpenGroups((current) => {
    const next = new Set(current);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    return next;
  });

  return (
    <div className="engineering-table-scroll">
      <table className="engineering-table zone-summary-table">
        <thead>
          <tr>
            <th scope="col">Zone</th>
            <th scope="col" className="numeric">Fixings</th>
            <th scope="col" className="numeric">Spacing</th>
            <th scope="col" className="numeric">Pressure</th>
            <th scope="col" className="numeric">Theoretical density</th>
            <th scope="col" className="numeric">Installable density</th>
          </tr>
        </thead>
        <tbody>
          {ZONE_CLASS_ORDER.flatMap((classID) => {
            const children = groups.get(classID) ?? [];
            if (children.length === 0) return [];
            const open = openGroups.has(classID);
            const classFixings = children.reduce((sum, zone) => sum + (zone.layout?.total_fixings ?? 0), 0);
            const selectedCommonSpacing = etaComponentMode ? undefined : commonLength(children, (zone) => zone.layout?.spacing_in_overlap);
            const selectedCommonPressure = commonQuantity(children, (zone) => zone.design_pressure ?? zone.characteristic_pressure);
            const commonTheoretical = commonQuantity(children, (zone) => zone.layout?.theoretical_density);
            const commonInstallable = etaComponentMode ? undefined : commonQuantity(children, (zone) => zone.layout?.installable_density);

            const rows: ReactNode[] = [
              <tr key={`class-${classID}`} className="zone-class-summary-row">
                <th scope="row">
                  <button
                    type="button"
                    className="zone-summary-zone-button zone-class-toggle"
                    aria-expanded={open}
                    onClick={() => toggleGroup(classID)}
                  >
                    <span aria-hidden="true">{open ? '▾' : '▸'}</span> {classID}
                  </button>
                </th>
                <td className="numeric">{formatCount(classFixings)}</td>
                <td className="numeric">{selectedCommonSpacing ? formatLength(selectedCommonSpacing) : '—'}</td>
                <td className="numeric">{selectedCommonPressure ? formatQuantityWithUnit(selectedCommonPressure) : '—'}</td>
                <td className="numeric">{commonTheoretical ? formatQuantityWithUnit(commonTheoretical) : '—'}</td>
                <td className="numeric">{commonInstallable ? formatQuantityWithUnit(commonInstallable) : '—'}</td>
              </tr>,
            ];

            if (open) {
              for (const zone of children) {
                const label = formatZoneDisplayLabel(zone.display_label ?? zone.zone_id);
                const selected = zone.zone_id === selectedZoneId;
                rows.push(
                  <tr key={`summary-${zone.zone_id}`} className="zone-class-child-row">
                    <th scope="row">
                      {onZoneSelect && zone.zone_id ? (
                        <button
                          type="button"
                          className="zone-summary-zone-button"
                          aria-pressed={selected}
                          onClick={() => onZoneSelect(selected ? undefined : zone.zone_id)}
                        >
                          {label}
                        </button>
                      ) : label}
                    </th>
                    <td className="numeric">{formatCount(zone.layout?.total_fixings)}</td>
                    <td className="numeric">{etaComponentMode ? '—' : formatLength(zone.layout?.spacing_in_overlap, '—')}</td>
                    <td className="numeric">{formatQuantityWithUnit(zone.design_pressure ?? zone.characteristic_pressure, '—')}</td>
                    <td className="numeric">{formatQuantityWithUnit(zone.layout?.theoretical_density, '—')}</td>
                    <td className="numeric">{etaComponentMode ? '—' : formatQuantityWithUnit(zone.layout?.installable_density, '—')}</td>
                  </tr>,
                );
              }
            }
            return rows;
          })}
          <tr className="zone-summary-total-row">
            <th scope="row">Total</th>
            <td className="numeric"><strong>{formatCount(totalFixings)}</strong></td>
            <td className="numeric">—</td>
            <td className="numeric">—</td>
            <td className="numeric">—</td>
            <td className="numeric">—</td>
          </tr>
        </tbody>
      </table>
    </div>
  );
}

function WindRow({ label, q }: { label: string; q: Parameters<typeof formatQuantity>[0] }) {
  if (!q) return null;
  return (
    <tr>
      <th scope="row">{label}{q.symbol && <> <Symbol symbol={q.symbol} /></>}</th>
      <td className="numeric">{formatQuantity(q)}</td>
      <td className="unit">{q.unit}</td>
    </tr>
  );
}

function ZoneRow({ zone, selected }: {
  zone: ZoneResult;
  selected: boolean;
}) {
  const blocked = zone.status === 'CALCULATION_STATUS_BLOCKED';
  const etaComponentMode = zone.resistance_basis === 'FIXING_RESISTANCE_BASIS_ETA_COMPONENT_CHARACTERISTIC';
  const rowClass = ['zone-row', selected ? 'is-selected' : ''].filter(Boolean).join(' ');
  const displayLabel = formatZoneDisplayLabel(zone.display_label ?? zone.zone_id);

  return (
    <details className={rowClass} open={selected || undefined}>
      <summary className="zone-row-summary">
        <span className={`zone-swatch zone-${zoneToken(zone)}`} aria-hidden="true" />
        <span className="zone-label">{displayLabel}</span>
        <span className="zone-utilization">
          {blocked ? statusLabel(zone.status) : etaComponentMode ? 'ETA pull-out basis' : formatUtilization(zone.utilization)}
        </span>
        <StatusBadge status={zone.status} />
      </summary>

      <div className="zone-row-detail">
        {blocked ? (
          <MessageList messages={zone.messages ?? []} emptyText="This zone was not verified." />
        ) : (
          <>
            <table className="engineering-table">
              <tbody>
                <tr><th scope="row">Zone area</th><td className="numeric">{formatArea(zone.area)}</td></tr>
                {etaComponentMode ? (
                  <>
                    <tr><th scope="row">Characteristic wind pressure</th><td className="numeric">{formatQuantityWithUnit(zone.characteristic_pressure)}</td></tr>
                    <tr><th scope="row">Characteristic component resistance</th><td className="numeric">{formatQuantityWithUnit(zone.governing_characteristic_resistance)}</td></tr>
                    {zone.governing_design_resistance && (
                      <tr><th scope="row">Component design resistance</th><td className="numeric">{formatQuantityWithUnit(zone.governing_design_resistance)}</td></tr>
                    )}
                  </>
                ) : (
                  <>
                    <tr><th scope="row">Design pressure</th><td className="numeric">{formatQuantityWithUnit(zone.design_pressure)}</td></tr>
                    <tr><th scope="row">Design resistance</th><td className="numeric">{formatQuantityWithUnit(zone.governing_design_resistance)}</td></tr>
                  </>
                )}
                <tr><th scope="row">{etaComponentMode ? 'Required component density' : 'Theoretical density'}</th><td className="numeric">{formatQuantityWithUnit(zone.layout?.theoretical_density)}</td></tr>
                {!etaComponentMode && (
                  <>
                    <tr><th scope="row">Installable density</th><td className="numeric">{formatQuantityWithUnit(zone.layout?.installable_density)}</td></tr>
                    <tr><th scope="row">Spacing in overlap</th><td className="numeric">{formatLength(zone.layout?.spacing_in_overlap)}</td></tr>
                  </>
                )}
                <tr><th scope="row">Fixings in this zone</th><td className="numeric">{formatCount(zone.layout?.total_fixings)} piece</td></tr>
              </tbody>
            </table>
            {etaComponentMode && (zone.layout?.adjustment_reasons ?? []).length > 0 && (
              <div className="adjustment-reasons">
                <div className="adjustment-reasons-title">Calculation basis</div>
                <ul>{zone.layout?.adjustment_reasons?.map((reason, index) => <li key={index}>{reason}</li>)}</ul>
              </div>
            )}
            {(zone.messages ?? []).length > 0 && <MessageList messages={zone.messages ?? []} emptyText="" />}
          </>
        )}
      </div>
    </details>
  );
}

function groupZones(zones: readonly ZoneResult[]): Map<ZoneClassID, ZoneResult[]> {
  const groups = new Map<ZoneClassID, ZoneResult[]>();
  for (const id of ZONE_CLASS_ORDER) groups.set(id, []);
  for (const zone of zones) groups.get(zoneClassID(zone))?.push(zone);
  return groups;
}

function zoneClassID(zone: ZoneResult): ZoneClassID {
  const label = (zone.display_label ?? '').trim().toUpperCase();
  const first = label[0];
  if (first === 'F' || first === 'G' || first === 'H' || first === 'I') return first;
  switch (zone.zone_class) {
    case 'WIND_ZONE_CLASS_CORNER': return 'F';
    case 'WIND_ZONE_CLASS_EDGE': return 'G';
    case 'WIND_ZONE_CLASS_INNER_EDGE': return 'H';
    default: return 'I';
  }
}

function commonQuantity(zones: readonly ZoneResult[], selector: (zone: ZoneResult) => Quantity | undefined): Quantity | undefined {
  const first = selector(zones[0]!);
  if (!first || first.value === undefined) return undefined;
  const allSame = zones.every((zone) => {
    const candidate = selector(zone);
    return candidate?.value !== undefined
      && Math.abs(candidate.value - first.value!) <= 1e-9
      && (candidate.unit ?? '') === (first.unit ?? '');
  });
  return allSame ? first : undefined;
}

function commonLength(zones: readonly ZoneResult[], selector: (zone: ZoneResult) => Length | undefined): Length | undefined {
  const first = selector(zones[0]!);
  if (!first || first.millimetres === undefined) return undefined;
  const allSame = zones.every((zone) => {
    const candidate = selector(zone);
    return candidate?.millimetres !== undefined
      && Math.abs(candidate.millimetres - first.millimetres!) <= 1e-9;
  });
  return allSame ? first : undefined;
}

function describeResistanceBasis(module: ModuleResult): string {
  switch (module.resistance_basis) {
    case 'FIXING_RESISTANCE_BASIS_COMPLETE_SYSTEM_DESIGN':
      return 'Verified / user-defined system';
    case 'FIXING_RESISTANCE_BASIS_ETA_COMPONENT_CHARACTERISTIC':
      return 'ETA pull-out';
    default:
      return 'Unavailable';
  }
}

function zoneToken(zone: ZoneResult): string {
  switch (zone.zone_class) {
    case 'WIND_ZONE_CLASS_CORNER': return 'corner';
    case 'WIND_ZONE_CLASS_EDGE': return 'edge';
    case 'WIND_ZONE_CLASS_INNER_EDGE': return 'inner-border';
    case 'WIND_ZONE_CLASS_INTERIOR': return 'interior';
    default: return 'interior';
  }
}

function formatQuantityLine(quantity: number | undefined, unit: string | undefined): string {
  if (quantity === undefined) return '—';
  const countLike = !unit || unit === 'pcs' || unit === 'piece' || unit === 'pieces';
  const value = quantity.toLocaleString('en-GB', {
    minimumFractionDigits: countLike ? 0 : 2,
    maximumFractionDigits: countLike ? 0 : 2,
  });
  if (!unit) return value;
  const displayUnit = unit === 'pcs' ? 'piece' : unit;
  return `${value} ${displayUnit}`;
}

function collectMessages(result: CalculationResponse | undefined): ValidationMessage[] {
  if (!result) return [];
  const collected: ValidationMessage[] = [...(result.messages ?? [])];
  for (const module of result.modules ?? []) {
    collected.push(...(module.messages ?? []));
    for (const zone of module.zones ?? []) collected.push(...(zone.messages ?? []));
  }

  const seen = new Set<string>();
  const messages: ValidationMessage[] = [];
  for (const message of collected) {
    const key = `${message.code ?? ''}\u0000${message.affected_object_id ?? ''}\u0000${message.explanation ?? ''}`;
    if (seen.has(key)) continue;
    seen.add(key);
    messages.push(message);
  }
  return messages;
}

function warningsOnly(messages: readonly ValidationMessage[]): ValidationMessage[] {
  return messages.filter((message) =>
    message.severity === 'SEVERITY_WARNING' || message.severity === 'SEVERITY_ERROR',
  );
}
